'use client'

/**
 * Client-direct WAPI calls for auth mutations (ui-react parity). Authenticate
 * with the user's token in the `X-TOKEN` header; no Nexus credential is involved.
 * `urlRoot` is the public WAPI base (e.g. https://api.wanderu.com/v2/, trailing slash).
 */

// Bound every request so a hung WAPI socket aborts (callers treat the throw as fail-safe)
// rather than stalling the sequential auto-login chain.
const WAPI_TIMEOUT_MS = 8000

/** WAPI app credentials (clientId/buildId/clientName) — public app identifiers, sent
 * in the federated auth body (ui-react parity). Not a user secret. */
export interface WapiClientCredentials {
    clientId: string
    buildId: string
    clientName: string
}

/** The session minted by WAPI from a federated credential. Token is held by the caller
 * (written to the mirror); it is never placed on the client-safe SessionContext. */
export interface FederatedSession {
    token: string
    identityId: string | null
    sessionId: string
    /** WAPI `expDt` as returned — an ISO-8601 string in practice (parsed by `readMirror`). */
    expiresAt: number | string | null
}

/**
 * Exchange a federated provider credential (Google) for a WAPI session, ui-react
 * parity: `POST {urlRoot}auth.json` with `{ credentials: { ...wapiCreds, provider, token,
 * type: 'federated' }, meta }`, `Content-Type: text/plain`, **no** `X-TOKEN` (there is no
 * session yet). Throws on a non-OK response or a missing token.
 */
export async function federatedLogin(opts: {
    urlRoot: string
    credentials: WapiClientCredentials
    provider: 'google'
    token: string
}): Promise<FederatedSession> {
    const response = await fetch(`${opts.urlRoot}auth.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
            credentials: {
                ...opts.credentials,
                provider: opts.provider,
                token: opts.token,
                type: 'federated',
            },
            meta: {},
        }),
        signal: AbortSignal.timeout(WAPI_TIMEOUT_MS),
    })

    if (!response.ok) {
        throw new Error(`WAPI federated auth failed: ${String(response.status)}`)
    }

    const data = (await response.json()) as {
        result?: {
            token?: string
            identityId?: string
            sessionId?: string
            expDt?: number | string
        }
    }
    const result = data.result
    if (!result?.token || !result.sessionId) {
        throw new Error('WAPI federated auth: response missing token/sessionId')
    }

    return {
        token: result.token,
        identityId: result.identityId ?? null,
        sessionId: result.sessionId,
        expiresAt: result.expDt ?? null,
    }
}

/**
 * Refresh an expired session token, ui-react parity (`AccountService.refreshToken`):
 * `POST {urlRoot}refresh.json` with `{ email, token, sessionId?, meta }`, `Content-Type:
 * text/plain`, **no** `X-TOKEN` (the expired token in the body is the credential). Returns
 * the refreshed session. Cognito-only — federated sessions re-restore via their SDK, not
 * refresh. Throws on a non-OK response or a missing token.
 */
export async function refreshRequest(opts: {
    urlRoot: string
    email: string
    token: string
    sessionId?: string
}): Promise<FederatedSession> {
    const response = await fetch(`${opts.urlRoot}refresh.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
            email: opts.email,
            token: opts.token,
            ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
            meta: {},
        }),
        signal: AbortSignal.timeout(WAPI_TIMEOUT_MS),
    })

    if (!response.ok) {
        throw new Error(`WAPI refresh failed: ${String(response.status)}`)
    }

    const data = (await response.json()) as {
        result?: {
            token?: string
            identityId?: string
            sessionId?: string
            expDt?: number | string
        }
    }
    const result = data.result
    if (!result?.token || !result.sessionId) {
        throw new Error('WAPI refresh: response missing token/sessionId')
    }

    return {
        token: result.token,
        identityId: result.identityId ?? null,
        sessionId: result.sessionId,
        expiresAt: result.expDt ?? null,
    }
}

/** The logged-in user's profile (subset Nexus needs for the navbar greeting). */
export interface UserInfo {
    firstName?: string
    lastName?: string
    email?: string
}

/**
 * Fetch the logged-in user's profile, ui-react parity (`AccountService.getUserInfo`):
 * `GET {urlRoot}userinfo.json` with the user token (`X-TOKEN`). The name is **not** in the
 * mirror, so this is how the navbar greets by name. Returns `{ firstName, lastName, email }`
 * (other profile fields are ignored). Throws on a non-OK response.
 */
export async function getUserInfo(opts: { urlRoot: string; token: string }): Promise<UserInfo> {
    const response = await fetch(`${opts.urlRoot}userinfo.json`, {
        method: 'GET',
        headers: { 'X-TOKEN': opts.token },
        signal: AbortSignal.timeout(WAPI_TIMEOUT_MS),
    })

    if (!response.ok) {
        throw new Error(`WAPI userinfo failed: ${String(response.status)}`)
    }

    const data = (await response.json()) as {
        result?: { firstName?: string; lastName?: string; email?: string }
    }
    const result = data.result ?? {}
    return {
        ...(typeof result.firstName === 'string' ? { firstName: result.firstName } : {}),
        ...(typeof result.lastName === 'string' ? { lastName: result.lastName } : {}),
        ...(typeof result.email === 'string' ? { email: result.email } : {}),
    }
}

/** Invalidate the session server-side: POST `/v2/logout.json` with the user token. */
export async function logoutRequest(urlRoot: string, token: string): Promise<void> {
    const response = await fetch(`${urlRoot}logout.json`, {
        method: 'POST',
        headers: {
            'X-TOKEN': token,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
        signal: AbortSignal.timeout(WAPI_TIMEOUT_MS),
    })

    if (!response.ok) {
        throw new Error(`WAPI logout failed: ${String(response.status)}`)
    }
}

/**
 * POST a `user:action:identify` event to `/v2/events.json` with the user token, at
 * ui-react parity. `data` carries the identity fields (cognito_identity_id, email,
 * source, sp_domain_id, sp_network_id, …). Throws on a non-OK response.
 */
export async function identifyRequest(opts: {
    urlRoot: string
    token: string
    data: Record<string, unknown>
}): Promise<void> {
    const response = await fetch(`${opts.urlRoot}events.json`, {
        method: 'POST',
        headers: {
            'X-TOKEN': opts.token,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            event: 'user:action:identify',
            event_timestamp: Date.now(),
            data: opts.data,
        }),
        signal: AbortSignal.timeout(WAPI_TIMEOUT_MS),
    })

    if (!response.ok) {
        throw new Error(`WAPI identify failed: ${String(response.status)}`)
    }
}
